<!--
  SectionSeparatorWrapper.vue

  This component is a wrapper that adds the following elements around provided children:
    - Separator line on top
    - Helper text on top (if helperText attribute and value is passed in)
    - Wrapping div for spacing of wrapped elements.
    - Separator line on bottom (if lastSection attribute is present)
  
  Following attributes are supported:
    - helperText: It can accept string of either text or raw html. End result would be wrapped with <p>.
                  (eg: if pass "test test <a href="#">link</a>", it would be <p>test test <a href="#">link</a></p>)
    - separator: If set (as 'separator' or ':separator=true' or ':separator=false'),
                 the component renders separators on the elements. Defaults to true
    - lastSection: If set (as 'lastSection' or 'lastSection=true' or 'lastSection=false'),
                   the component renders a bottom separator too given 'seperator' is true. Defaults to false

-->

<template>
  <section class="focusable" :class="lastSection ? 'last-section' : null">
    <div class="top-separator separator-wrapper" :class="{ 'full-height': fullHeight }">
      <hr v-if="separator && topSeparator" class="separator" />

      <div v-if="helperText" class="helper-text" :class="helperText ? null : 'empty'">
        <p v-if="helperText" v-html="helperText"></p>
      </div>

      <div v-if="this.$slots.helperText" class="helper-text">
        <slot v-if="this.$slots.helperText" name="helperText" />
      </div>

      <div v-if="fullHeight" class="full-height-slot-contents" :class="separator ? 'separator-content' : null">
        <slot />
      </div>
    </div>

    <!--Don't display the slot below if it's fullHeight, as it needs to be added to the above grid-->
    <div :class="separator ? 'separator-content' : null" v-if="!fullHeight">
      <div class="separator-sub-header" :class="subHeading ? 'sub-header' : 'main-header'">
        <h2 v-if="headerText" :class="subHeading ? 'h6 sub-heading-underlined' : 'h4'">{{ headerText }}</h2>
        <p v-if="headerRequiredFieldsText">* {{ headerRequiredFieldsText }}</p>
      </div>
      <slot />
    </div>
    <div v-if="lastSection && separator" class="separator-wrapper">
      <hr class="separator" />
    </div>
  </section>
</template>

<script>
export default {
  name: 'SectionSeparatorWrapper',

  props: {
    helperText: {
      type: String,
    },

    headerText: {
      type: String,
    },

    headerRequiredFieldsText: {
      type: String,
    },

    subHeading: {
      type: Boolean,
      default: false,
    },

    separator: {
      type: Boolean,
      default: false,
    },

    topSeparator: {
      type: Boolean,
      default: true,
    },

    lastSection: {
      type: Boolean,
      default: false,
    },

    /**
     * Flag to set to expand the section(slot) for the whole height of helperText
     * @type Boolean
     * @default false
     */
    fullHeight: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
section {
  &.last-section {
    margin-bottom: spacer(3);
  }

  &.focusable {
    > .separator-wrapper {
      @include grid-layout(null, spacer(2));
      @include grid-template(2fr 1fr);
      height: 0;

      &.full-height {
        height: fit-content;

        .full-height-slot-contents {
          @include grid-position(1, 1);

          @include mobile {
            @include grid-position(1, 2);
          }

          &.separator-content {
            padding-top: spacer(2.5); // 25px
          }

          .form-field {
            grid-template-columns: 1fr;
          }
        }
      }

      > hr {
        @include grid-position(1, 1);

        &.separator {
          display: block;
          width: 100%;
          height: 1px;
          border: 0;
          margin: 0;
          background: none;
          border-top: 1px solid $GREY_700;
        }
      }

      > .helper-text {
        @include grid-position(2, 1);
        &.empty {
          height: 0;
          margin: 0;
        }

        & > p,
        & > div {
          background-color: $WHITE;
          padding: 12px 24px;
          border-left: 3px solid $LIGHT_GREEN;
          border-radius: 2px;
          font-weight: $NORMAL;
          color: $GREY_800;
          box-shadow: 0 2px 6px rgba($BLACK, 0.06);

          + p {
            padding-top: 0.5em;
          }
        }
      }

      @include mobile {
        height: unset;

        > hr {
          @include grid-position(1, 2);
        }

        > .helper-text {
          @include grid-position(1, 1);
        }
      }
    }

    > .separator-content {
      padding-top: spacer(2.5); // 25px

      > h2 {
        font-size: font-size(2);
        margin-top: 0;
        margin-bottom: 0.25rem;
        @include grid-layout;
        @include grid-template(2fr 1fr);

        @include mobile {
          font-size: font-size(1);
        }
      }

      & > *:last-child {
        margin-bottom: spacer(3);
      }
    }

    &:focus-within {
      > .separator-wrapper {
        > hr {
          border-color: $CRIMSON;
        }
      }

      + section.focusable {
        > .top-separator {
          > hr {
            border-color: $CRIMSON;
          }
        }
      }
    }
  }
}

.separator-header {
  h2 {
    margin-bottom: 0;
  }

  p {
    margin-bottom: 0;
  }
}

.main-header {
  margin-bottom: 48px;
}

.sub-header {
  margin-bottom: 32px;
}
</style>
